'use client';

import React, { useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';

import { Typography } from 'src/shared/ui/Typography';

import { CheckIcon } from './Icon';
import styles from './index.module.css';
import type { CommonCheckboxProps } from './typing';

export const Checkbox = React.forwardRef<HTMLLabelElement, CommonCheckboxProps>(
  (
    {
      checked,
      onChange,
      view = 'square',
      label,
      hint,
      description,
      stretched,
      name,
      className,
      fieldClassName,
      disabled,
      labelProps,
      error,
      inputRef,
      ...restProps
    },
    ref,
  ) => {
    const labelRef = useRef<HTMLLabelElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e, { checked: e.target.checked, name });
      }
    };

    const errorMessage = typeof error === 'boolean' ? '' : error;

    return (
      <div className={styles.wrapper}>
        <label
          className={cn(styles.component, className, {
            [styles.disabled]: disabled,
            [styles.checked]: checked,
            [styles.stretched]: stretched,
          })}
          ref={mergeRefs([labelRef, ref, labelProps?.ref as React.Ref<HTMLLabelElement>])}
        >
          <input
            type="checkbox"
            onChange={handleChange}
            className={styles.input}
            disabled={disabled}
            checked={checked}
            ref={inputRef}
            {...restProps}
          />
          <span className={cn(styles.box, styles[view], fieldClassName)}>
            {checked && <CheckIcon className={styles.icon} />}
          </span>
          {(label || hint || errorMessage) && (
            <span className={styles.content}>
              {label && (
                <Typography tag="span" view="reg_14" className={styles.label}>
                  {label}
                </Typography>
              )}
            </span>
          )}
        </label>
        {hint && !errorMessage && (
          <Typography tag="span" view="reg_14" className={styles.hint}>
            {hint}
          </Typography>
        )}
        {errorMessage && (
          <Typography tag="span" view="reg_14" className={styles.error}>
            {errorMessage}
          </Typography>
        )}
        {description && (
          <Typography tag="span" color="var(--color-dark-gray)" view="reg_14">
            {description}
          </Typography>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
